/**
 * Geocodificador basado en OpenStreetMap vía Overpass API
 * 
 * OpenStreetMap es una fuente colaborativa con excelente cobertura
 * para infraestructuras que no están en registros oficiales.
 * 
 * CRÍTICO para localizar:
 * - Antenas de telecomunicaciones (tower:type=communication)
 * - Instalaciones deportivas menores no registradas
 * - Industrias y polígonos industriales
 * - Equipamientos municipales diversos
 * - Cualquier infraestructura mapeada por la comunidad
 * 
 * Servicios:
 * - Overpass API: https://overpass-api.de/api/interpreter
 * - Nominatim (geocoding): https://nominatim.openstreetmap.org/
 * 
 * Limitaciones:
 * - Overpass: ~10,000 requests/día (compartido)
 * - Nominatim: 1 request/segundo (política de uso)
 * 
 * @module services/geocoding/specialized
 */

import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { FastFuzzy } from '../../../lib/fuzzySearch';
import { acquireHostSlot, reportRateLimited, retryAfterHeaderOf } from '../HostRateGate';
import {
  leerCacheNominatim,
  guardarAciertoNominatim,
  guardarSinResultadoNominatim,
} from '../cache/NominatimCache';
import type { GeocodingResult} from '../../../types/infrastructure';
import { InfrastructureType } from '../../../types/infrastructure';
import proj4 from 'proj4';
import type { OverpassOSMElement } from '../../../types/geojson';

/**
 * P2-OVERPASS-SILENCE: Silencia errores de red transitorios (429/504/400)
 * en entornos de test para reducir el log de ~100 k a ~5 k líneas.
 * En producción y desarrollo se mantiene console.error normal.
 */
const _IS_TEST = import.meta.env.MODE === 'test' || import.meta.env.VITEST === 'true';
const _overpassLog = (msg: string, err?: unknown): void => {
  if (!_IS_TEST) console.error(msg, err);
};

// ============================================================================
// CORTEOVP · corte rápido de Overpass (DOP selladas por Luis, 27-oct-2026)
// ============================================================================
// Estado de módulo, compartido por TODAS las instancias: así cubre el NIVEL 4
// del orquestador, `HydraulicMunicipalGeocoder.searchOSM` y `TorresComGeocoder`
// sin cambiar firmas (DOP-1 = a).
// DOP-2: se abre al primer timeout o 502/504/521 (el 429 sigue con su
//   reintento); esperas crecientes 1, 2, 5 y 10 min; un éxito lo reinicia;
//   al vencer la espera pasa una sola petición de prueba.
// DOP-3: 8 s para las consultas que hace cada fila; 30 s para las municipales.
// DOP-4: con el corte abierto se responde vacío al instante; un aviso al abrir
//   y otro al cerrar. Las peticiones ya en vuelo no se cancelan.

/** Esperas del corte tras cada apertura consecutiva (ms). */
export const OVERPASS_ESPERAS_CORTE_MS: readonly number[] = [60_000, 120_000, 300_000, 600_000];
/** Tiempo máximo de una consulta hecha por fila (por debajo del tope de 10 s de la fila). */
export const OVERPASS_TIMEOUT_FILA_MS = 8_000;
/** Tiempo máximo de una consulta municipal o por área. */
export const OVERPASS_TIMEOUT_MUNICIPAL_MS = 30_000;
const OVERPASS_ESTADOS_QUE_ABREN: ReadonlySet<number> = new Set([502, 504, 521]);

const _corteOverpass = {
  abierto: false,
  abiertoHasta: 0,
  aperturasSeguidas: 0,
  sondaEnVuelo: false,
};

function reiniciarCorteOverpass(): void {
  _corteOverpass.abierto = false;
  _corteOverpass.abiertoHasta = 0;
  _corteOverpass.aperturasSeguidas = 0;
  _corteOverpass.sondaEnVuelo = false;
}

/** Reinicia el corte (solo para pruebas; precedente `_resetHostRateGateForTests`). */
export function _resetCorteOverpassForTests(): void {
  reiniciarCorteOverpass();
}

/** Indica si el corte está abierto ahora mismo (lectura para pruebas y diagnóstico). */
export function corteOverpassAbierto(): boolean {
  return _corteOverpass.abierto;
}

/**
 * ¿Puede salir una petición a Overpass? `'libre'` con el corte cerrado;
 * `'sonda'` para la única de prueba al vencer la espera; `null` si no sale.
 */
function corteOverpassDejaPasar(): 'libre' | 'sonda' | null {
  if (!_corteOverpass.abierto) return 'libre';
  if (Date.now() < _corteOverpass.abiertoHasta) return null;
  if (_corteOverpass.sondaEnVuelo) return null;
  _corteOverpass.sondaEnVuelo = true;
  return 'sonda';
}

function registrarExitoOverpass(): void {
  if (_corteOverpass.abierto) {
    console.warn('[OverpassGeocoder] Overpass vuelve a responder: corte cerrado');
  }
  reiniciarCorteOverpass();
}

function esFalloQueAbreCorte(error: unknown): boolean {
  const e = error as { code?: string; response?: { status?: number } };
  if (e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') return true;
  const status = e?.response?.status;
  return status !== undefined && OVERPASS_ESTADOS_QUE_ABREN.has(status);
}

function registrarFalloOverpass(error: unknown, eraSonda: boolean): void {
  if (!esFalloQueAbreCorte(error)) {
    // Otro fallo (429, 4xx, red): no abre; si era la prueba, se permite otra.
    if (eraSonda) _corteOverpass.sondaEnVuelo = false;
    return;
  }
  const yaAbierto = _corteOverpass.abierto;
  // Rezagados que salieron antes de abrirse el corte: no escalan la espera.
  if (yaAbierto && !eraSonda) return;
  const indice = Math.min(_corteOverpass.aperturasSeguidas, OVERPASS_ESPERAS_CORTE_MS.length - 1);
  const esperaMs = OVERPASS_ESPERAS_CORTE_MS[indice];
  _corteOverpass.aperturasSeguidas += 1;
  _corteOverpass.abierto = true;
  _corteOverpass.abiertoHasta = Date.now() + esperaMs;
  _corteOverpass.sondaEnVuelo = false;
  if (!yaAbierto) {
    console.warn(
      `[OverpassGeocoder] Overpass no responde: corte abierto ${esperaMs / 60_000} min; las consultas a OSM se omiten mientras tanto`
    );
  }
}

/**
 * Elemento OSM parseado
 */
export interface OSMElement {
  id: number;
  type: 'node' | 'way' | 'relation';
  name: string;
  lat: number;
  lon: number;
  tags: Record<string, string>;
}

/**
 * Categorías de infraestructura de telecomunicaciones
 * F3.T3.2: Clasificación automática según tags OSM
 */
export enum TelecomCategory {
  /** Telefonía móvil (2G/3G/4G/5G) */
  MOBILE = 'MOBILE',
  /** Radio comunicaciones (amateur, CB, PMR) */
  RADIO = 'RADIO',
  /** Servicios de emergencia (TETRA, policía, bomberos) */
  EMERGENCY = 'EMERGENCY',
  /** Difusión (TV, radio FM/AM) */
  BROADCAST = 'BROADCAST',
  /** Internet/datos (microondas, enlaces punto a punto) */
  DATA = 'DATA',
  /** No clasificable */
  UNKNOWN = 'UNKNOWN'
}

/**
 * Opciones de búsqueda en Overpass
 */
export interface OverpassSearchOptions {
  /** Nombre a buscar */
  name: string;
  
  /** Municipio para filtrar */
  municipality: string;
  
  /** Provincia (opcional, mejora precisión del área) */
  province?: string;
  
  /** Tipo de infraestructura PTEL */
  infrastructureType?: InfrastructureType;
  
  /** Radio de búsqueda en metros (para búsqueda por proximidad) */
  radius?: number;
  
  /** Coordenadas centrales para búsqueda por proximidad */
  center?: { lat: number; lon: number };
  
  /** Límite de resultados */
  maxResults?: number;
}

/**
 * Queries específicas para antenas de telecomunicaciones
 * 
 * F3.T3: Query especializada para máxima cobertura de infraestructuras
 * de telecomunicaciones en OSM.
 * 
 * Tags OSM relevantes para antenas:
 * - man_made=mast (mástiles genéricos, incluye telecom)
 * - man_made=tower + tower:type=communication (torres específicas)
 * - telecom=antenna (antenas individuales)
 * - telecom=mast (mástiles de telecomunicaciones)
 * - telecom=pole (postes con antenas)
 * - communication:mobile_phone=yes (instalaciones móviles)
 * - tower:type=communication (clasificación de torre)
 */
const TELECOM_ANTENNA_QUERIES = [
  // Mástiles de telecomunicaciones (forma más común)
  'node["man_made"="mast"]',
  'way["man_made"="mast"]',
  // Torres de comunicación específicas
  'node["man_made"="tower"]["tower:type"="communication"]',
  'way["man_made"="tower"]["tower:type"="communication"]',
  // Antenas explícitas
  'node["telecom"="antenna"]',
  'node["telecom"="mast"]',
  'node["telecom"="pole"]',
  // Torres con tipo comunicación
  'node["tower:type"="communication"]',
  'way["tower:type"="communication"]',
  // Instalaciones de telefonía móvil
  'node["communication:mobile_phone"="yes"]',
  // Estaciones base
  'node["telecom"="base_station"]',
  // Repetidores
  'node["telecom"="repeater"]'
];

/**
 * Query compacta para findTelecomTowers (mantener compatibilidad)
 */
const TELECOM_QUERY = TELECOM_ANTENNA_QUERIES.join(';\n  ') + ';';

/**
 * FIX #4: Construye queries Overpass con sintaxis correcta
 * El filtro (area.searchArea) debe estar DENTRO de cada statement,
 * no al final del bloque union.
 * 
 * @param queries - Array de queries Overpass (ej: ['nwr["amenity"="hospital"]'])
 * @param areaName - Nombre del área de búsqueda (default: 'searchArea')
 * @returns String con queries correctamente filtradas
 */
function buildOverpassAreaQuery(queries: string[], areaName: string = 'searchArea'): string {
  return queries
    .map(q => `${q}(area.${areaName})`)
    .join(';\n  ');
}

/**
 * Mapeo de tipos PTEL a queries Overpass
 * @public Exportado para testing
 */
export const PTEL_TO_OSM_QUERIES: Record<InfrastructureType, string[]> = {
  [InfrastructureType.HEALTH]: [
    'nwr["amenity"="hospital"]',
    'nwr["amenity"="clinic"]',
    'nwr["amenity"="doctors"]',
    'nwr["healthcare"="centre"]'
  ],
  [InfrastructureType.EDUCATION]: [
    'nwr["amenity"="school"]',
    'nwr["amenity"="kindergarten"]',
    'nwr["amenity"="college"]',
    'nwr["amenity"="university"]'
  ],
  [InfrastructureType.POLICE]: [
    'nwr["amenity"="police"]'
  ],
  [InfrastructureType.FIRE]: [
    'nwr["amenity"="fire_station"]'
  ],
  [InfrastructureType.CULTURAL]: [
    'nwr["amenity"="library"]',
    'nwr["amenity"="theatre"]',
    'nwr["amenity"="community_centre"]',
    'nwr["tourism"="museum"]'
  ],
  [InfrastructureType.RELIGIOUS]: [
    'nwr["amenity"="place_of_worship"]',
    'nwr["building"="church"]',
    'nwr["building"="chapel"]'
  ],
  [InfrastructureType.SPORTS]: [
    'nwr["leisure"="sports_centre"]',
    'nwr["leisure"="swimming_pool"]',
    'nwr["leisure"="pitch"]',
    'nwr["leisure"="stadium"]'
  ],
  [InfrastructureType.MUNICIPAL]: [
    'nwr["amenity"="townhall"]',
    'nwr["office"="government"]'
  ],
  [InfrastructureType.SOCIAL]: [
    'nwr["amenity"="social_facility"]',
    'nwr["social_facility"="nursing_home"]'
  ],
  [InfrastructureType.FUEL]: [
    'nwr["amenity"="fuel"]'
  ],
  [InfrastructureType.EMERGENCY]: [
    'nwr["emergency"="ambulance_station"]',
    'nwr["amenity"="emergency_service"]'
  ],
  [InfrastructureType.MAREMOTO]: [
    // OSM no tiene tag dedicado a maremoto/tsunami como tal; fallback a zonas de riesgo costero
    'nwr["natural"="coastline"]',
    'nwr["hazard"~"tsunami|flood"]'
  ],
  [InfrastructureType.HYDRAULIC]: [
    'nwr["man_made"="wastewater_plant"]',
    'nwr["man_made"="water_works"]',
    'nwr["man_made"="reservoir_covered"]'
  ],
  [InfrastructureType.ENERGY]: [
    'nwr["power"="substation"]',
    'nwr["power"="plant"]',
    'nwr["generator:source"="solar"]',
    'nwr["generator:source"="wind"]'
  ],
  [InfrastructureType.GENERIC]: [
    'nwr["amenity"]',
    'nwr["building"="public"]'
  ],
  [InfrastructureType.TELECOM]: TELECOM_ANTENNA_QUERIES,
  [InfrastructureType.INDUSTRIAL]: [
    'nwr["landuse"="industrial"]',
    'nwr["building"="industrial"]',
    'nwr["industrial"]'
  ],
  [InfrastructureType.VIAL]: [
    'nwr["highway"="primary"]',
    'nwr["highway"="secondary"]',
    'nwr["highway"="tertiary"]'
  ],
  [InfrastructureType.RSU]: [
    'nwr["amenity"="recycling"]',
    'nwr["amenity"="waste_disposal"]',
    'nwr["landuse"="landfill"]',
    'nwr["man_made"="wastewater_plant"]'
  ],
  [InfrastructureType.PORT]: [
    'nwr["harbour"]',
    'nwr["landuse"="harbour"]',
    'nwr["seamark:type"="harbour"]',
    'nwr["amenity"="ferry_terminal"]',
    'nwr["leisure"="marina"]',
    'nwr["industrial"="port"]'
  ]
};

/**
 * Query para instalaciones industriales (como array para FIX #4)
 */
const INDUSTRIAL_QUERIES = [
  'nwr["landuse"="industrial"]',
  'nwr["building"="industrial"]',
  'nwr["industrial"]'
];

/**
 * Geocodificador basado en OpenStreetMap/Overpass
 * 
 * Precisión esperada: Variable (±5-100m según elemento)
 * Cobertura: Depende del mapeo comunitario (excelente en zonas urbanas)
 * 
 * @example
 * ```typescript
 * const geocoder = new OverpassGeocoder();
 * 
 * // Buscar antenas de telecomunicaciones
 * const antenas = await geocoder.findTelecomTowers('Colomera', 'Granada');
 * 
 * // Buscar cualquier infraestructura por nombre
 * const result = await geocoder.geocode({
 *   name: 'Piscina Municipal',
 *   municipality: 'Colomera',
 *   infrastructureType: InfrastructureType.SPORTS
 * });
 * ```
 */
export class OverpassGeocoder {
  private axiosInstance: AxiosInstance;
  private nominatimInstance: AxiosInstance;
  
  /**
   * Proyección para conversión WGS84 ↔ UTM30
   */
  private static readonly WGS84 = 'EPSG:4326';
  private static readonly UTM30 = 'EPSG:25830';

  /**
   * URLs de servicios
   */
  private static readonly OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
  private static readonly NOMINATIM_URL = 'https://nominatim.openstreetmap.org';

  /**
   * Caché de áreas de municipios (evita buscar el área repetidamente)
   */
  private areaCache: Map<string, number> = new Map();

  constructor() {
    // Cliente para Overpass (sin límite de rate estricto)
    this.axiosInstance = axios.create({
      timeout: 30000, // 30s para queries complejas
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    // Cliente para Nominatim (1 req/s)
    this.nominatimInstance = axios.create({
      timeout: 10000,
      headers: {
      }
    });

    // Configurar proyección
    if (!proj4.defs(OverpassGeocoder.UTM30)) {
      proj4.defs(OverpassGeocoder.UTM30, '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
    }
  }

  /**
   * Geocodifica una infraestructura usando Overpass + fuzzy matching
   */
  public async geocode(options: OverpassSearchOptions): Promise<GeocodingResult | null> {
    try {
      // Construir y ejecutar query
      const query = await this.buildQuery(options);
      const elements = await this.executeQuery(query);

      if (elements.length === 0) {
        return null;
      }

      // Aplicar fuzzy matching para encontrar el mejor resultado
      const bestMatch = this.findBestMatch(options.name, elements);

      if (!bestMatch) {
        return null;
      }

      // Convertir coordenadas a UTM30
      const [x, y] = proj4(OverpassGeocoder.WGS84, OverpassGeocoder.UTM30, [bestMatch.element.lon, bestMatch.element.lat]);

      return {
        x,
        y,
        confidence: Math.round(bestMatch.score * 100),
        source: 'OpenStreetMap',
        sourceLayer: bestMatch.element.type,
        matchedName: bestMatch.element.name,
        fuzzyScore: bestMatch.score,
        municipality: options.municipality,
        province: options.province || '',
        metadata: {
          osmId: bestMatch.element.id,
          osmType: bestMatch.element.type,
          tags: bestMatch.element.tags
        }
      };

    } catch (error) {
      _overpassLog('Error geocodificando vía Overpass:', error);
      return null;
    }
  }

  /**
   * Busca antenas de telecomunicaciones en un municipio
   * 
   * F3.T3: Método especializado para infraestructuras de telecomunicaciones
   * usando queries optimizadas con admin_level=8 para precisión municipal.
   * 
   * Tags buscados:
   * - man_made=mast (mástiles)
   * - man_made=tower + tower:type=communication (torres)
   * - telecom=antenna/mast/pole (antenas, mástiles, postes)
   * - communication:mobile_phone=yes (telefonía móvil)
   * 
   * @param municipality - Nombre del municipio (exacto)
   * @param province - Provincia (opcional, mejora desambiguación)
   * @returns Array de elementos OSM con coordenadas WGS84
   * 
   * @example
   * ```typescript
   * const antenas = await geocoder.findTelecomTowers('Colomera', 'Granada');
   * // Retorna mástiles, torres y antenas en Colomera
   * ```
   */
  public async findTelecomTowers(
    municipality: string,
    province?: string
  ): Promise<OSMElement[]> {
    try {
      // F3.T3: Usar admin_level=8 para precisión a nivel municipal
      // y añadir boundary=administrative para robustez
      const areaFilter = province 
        ? `area["name"="${municipality}"]["admin_level"="8"]["boundary"="administrative"]->.searchArea;`
        : `area["name"="${municipality}"]["admin_level"="8"]->.searchArea;`;

      const query = `
        [out:json][timeout:60];
        ${areaFilter}
        (
          ${buildOverpassAreaQuery(TELECOM_ANTENNA_QUERIES)}
        );
        out center;
      `;

      const elements = await this.executeQuery(query);

      // Log para diagnóstico
      if (elements.length > 0) {
        console.log(`[OverpassGeocoder] Encontradas ${elements.length} antenas/torres en ${municipality}`);
      }

      return elements;

    } catch (error) {
      _overpassLog(`Error buscando antenas en ${municipality}:`, error);
      return [];
    }
  }

  /**
   * Busca antenas de telecomunicaciones con elementos sin nombre
   * 
   * F3.T3: Variante que incluye elementos OSM sin nombre,
   * útil para mástiles anónimos que solo tienen coordenadas.
   * 
   * @param municipality - Nombre del municipio
   * @param province - Provincia (opcional)
   * @param timeoutMs - Tiempo máximo de la consulta. Por defecto el municipal
   *   (30 s, lote de P2); ANTMUN: la ruta de fila de TorresCom pasa
   *   `OVERPASS_TIMEOUT_FILA_MS` para no superar el tope de 10 s de la fila.
   * @returns Array completo incluyendo elementos anónimos
   */
  public async findAllTelecomInfrastructure(
    municipality: string,
    province?: string,
    timeoutMs: number = OVERPASS_TIMEOUT_MUNICIPAL_MS
  ): Promise<Array<OSMElement & { anonymous?: boolean }>> {
    try {
      const areaFilter = province 
        ? `area["name"="${municipality}"]["admin_level"="8"]["boundary"="administrative"]->.searchArea;`
        : `area["name"="${municipality}"]["admin_level"="8"]->.searchArea;`;

      const query = `
        [out:json][timeout:60];
        ${areaFilter}
        (
          ${buildOverpassAreaQuery(TELECOM_ANTENNA_QUERIES)}
        );
        out center;
      `;

      const response = await this.postOverpass(query, timeoutMs);

      if (!response || !response.data || !response.data.elements) {
        return [];
      }

      // Parsear incluyendo elementos sin nombre
      return response.data.elements
        .map((el: OverpassOSMElement) => this.parseElementIncludingAnonymous(el))
        .filter((el: (OSMElement & { anonymous?: boolean }) | null) => el !== null);

    } catch (error) {
      _overpassLog(`Error buscando infraestructura telecom en ${municipality}:`, error);
      return [];
    }
  }

  /**
   * Busca antenas de telecomunicaciones cercanas a coordenadas UTM30
   * 
   * F3.T3.1: Método para validación/corrección de coordenadas PTEL.
   * Útil cuando un documento tiene coordenadas dudosas y queremos
   * verificar si existe infraestructura OSM cercana.
   * 
   * @param coords - Coordenadas UTM30 (EPSG:25830)
   * @param radiusMeters - Radio de búsqueda en metros (default 500m, max 5000m)
   * @returns Array de elementos ordenados por distancia ascendente
   * 
   * @example
   * ```typescript
   * // Validar coordenadas de "Repetidor Municipal" en documento PTEL
   * const cercanas = await geocoder.findNearbyTelecom(
   *   { x: 445000, y: 4145000 },
   *   500
   * );
   * if (cercanas.length > 0 && cercanas[0].distanceMeters < 50) {
   *   console.log('Coordenadas válidas, antena encontrada:', cercanas[0].name);
   * }
   * ```
   */
  public async findNearbyTelecom(
    coords: { x: number; y: number },
    radiusMeters: number = 500
  ): Promise<Array<OSMElement & { distanceMeters: number }>> {
    try {
      // Limitar radio máximo para evitar queries muy pesadas
      const radius = Math.min(Math.max(radiusMeters, 10), 5000);
      
      // Convertir UTM30 a WGS84 para query Overpass
      const { lon, lat } = this.toWGS84(coords.x, coords.y);
      
      // Query por proximidad (around) - más eficiente que BBOX para radios pequeños
      const query = `
        [out:json][timeout:30];
        (
          ${TELECOM_QUERY}
        )(around:${radius},${lat},${lon});
        out center;
      `;

      const elements = await this.executeQuery(query);
      
      // Calcular distancia y ordenar
      const withDistance = elements.map(el => {
        const elCoords = this.toUTM30(el.lon, el.lat);
        const distanceMeters = Math.sqrt(
          Math.pow(elCoords.x - coords.x, 2) + 
          Math.pow(elCoords.y - coords.y, 2)
        );
        return { ...el, distanceMeters: Math.round(distanceMeters) };
      });
      
      // Ordenar por distancia ascendente
      withDistance.sort((a, b) => a.distanceMeters - b.distanceMeters);
      
      if (withDistance.length > 0) {
        console.log(`[OverpassGeocoder] Encontradas ${withDistance.length} antenas en radio ${radius}m`);
      }
      
      return withDistance;

    } catch (error) {
      _overpassLog('Error buscando antenas cercanas:', error);
      return [];
    }
  }

  /**
   * Clasifica el tipo de infraestructura de telecomunicaciones
   * 
   * F3.T3.2: Clasificación automática según tags OSM para
   * distinguir entre telefonía móvil, radio, emergencias, etc.
   * 
   * Prioridad de detección:
   * 1. Tags explícitos (communication:mobile_phone, emergency, etc.)
   * 2. Operadores conocidos (Telefónica, Vodafone, etc.)
   * 3. Tipo de torre/uso
   * 4. Nombre del elemento
   * 
   * @param element - Elemento OSM con tags
   * @returns Categoría de telecomunicaciones
   * 
   * @example
   * ```typescript
   * const antenas = await geocoder.findTelecomTowers('Colomera');
   * antenas.forEach(a => {
   *   const tipo = geocoder.classifyTelecomType(a);
   *   console.log(`${a.name}: ${tipo}`);
   * });
   * ```
   */
  public classifyTelecomType(element: OSMElement): TelecomCategory {
    const tags = element.tags || {};
    const name = (element.name || '').toLowerCase();
    
    // 1. Tags explícitos de telefonía móvil
    if (
      tags['communication:mobile_phone'] === 'yes' ||
      tags['communication:gsm'] === 'yes' ||
      tags['communication:umts'] === 'yes' ||
      tags['communication:lte'] === 'yes' ||
      tags['communication:5g'] === 'yes' ||
      tags['telecom'] === 'base_station'
    ) {
      return TelecomCategory.MOBILE;
    }
    
    // 2. Tags de emergencias (TETRA, DMR/REJA, policía, bomberos)
    // Incluye terminología específica andaluza (REJA, GREA, INFOCA)
    if (
      // Tags explícitos de redes de emergencias
      tags['communication:tetra'] === 'yes' ||
      tags['communication:dmr'] === 'yes' ||
      tags['emergency'] ||
      // Operadores de emergencias (genéricos)
      tags['operator']?.toLowerCase().includes('emergencia') ||
      tags['operator']?.toLowerCase().includes('112') ||
      tags['operator']?.toLowerCase().includes('polic') ||
      tags['operator']?.toLowerCase().includes('bombero') ||
      tags['operator']?.toLowerCase().includes('guardia civil') ||
      // Operadores específicos Andalucía (REJA y servicios conectados)
      tags['operator']?.toLowerCase().includes('reja') ||
      tags['operator']?.toLowerCase().includes('grea') ||
      tags['operator']?.toLowerCase().includes('infoca') ||
      tags['operator']?.toLowerCase().includes('epes') ||
      tags['operator']?.toLowerCase().includes('061') ||
      tags['operator']?.toLowerCase().includes('protección civil') ||
      tags['operator']?.toLowerCase().includes('proteccion civil') ||
      // Nombres que indican emergencias (genéricos)
      name.includes('tetra') ||
      name.includes('emergencia') ||
      name.includes('112') ||
      // Nombres específicos Andalucía
      name.includes('reja') ||
      name.includes('dmr') ||
      name.includes('red emergencia') ||
      name.includes('repetidor municipal') ||
      name.includes('repetidor emergencia') ||
      name.includes('grea') ||
      name.includes('infoca') ||
      name.includes('protección civil') ||
      name.includes('proteccion civil')
    ) {
      return TelecomCategory.EMERGENCY;
    }
    
    // 3. Tags de difusión (TV, radio FM/AM)
    if (
      tags['communication:television'] === 'yes' ||
      tags['communication:radio'] === 'yes' ||
      tags['communication:fm'] === 'yes' ||
      tags['communication:dab'] === 'yes' ||
      tags['tower:type'] === 'communication' && tags['communication:radio'] ||
      name.includes('rtve') ||
      name.includes('televisi') ||
      name.includes(' fm') ||
      name.includes('radio ') ||
      name.includes('difusi')
    ) {
      return TelecomCategory.BROADCAST;
    }
    
    // 4. Tags de radioaficionados / radio comunicaciones
    if (
      tags['communication:amateur_radio'] === 'yes' ||
      tags['amateur_radio'] ||
      tags['radio:amateur'] === 'yes' ||
      name.includes('radioaficionado') ||
      name.includes('radio club') ||
      name.includes('pmr') ||
      name.includes('cb ')
    ) {
      return TelecomCategory.RADIO;
    }
    
    // 5. Tags de datos/microondas
    if (
      tags['communication:microwave'] === 'yes' ||
      tags['communication:wifi'] === 'yes' ||
      tags['telecom'] === 'data_center' ||
      tags['telecom'] === 'exchange' ||
      name.includes('microondas') ||
      name.includes('enlace') ||
      name.includes('wimax') ||
      name.includes('wifi')
    ) {
      return TelecomCategory.DATA;
    }
    
    // 6. Detección por operador conocido (telefonía móvil)
    const operator = (tags['operator'] || '').toLowerCase();
    const mobileOperators = [
      'telefónica', 'telefonica', 'movistar',
      'vodafone', 'orange', 'masmovil', 'mas movil',
      'yoigo', 'jazztel', 'digi', 'simyo', 'pepephone',
      'cellnex', 'axion', 'abertis'
    ];
    
    if (mobileOperators.some(op => operator.includes(op))) {
      return TelecomCategory.MOBILE;
    }
    
    // 7. Detección por nombre común
    if (
      name.includes('móvil') ||
      name.includes('movil') ||
      name.includes('gsm') ||
      name.includes('4g') ||
      name.includes('5g') ||
      name.includes('lte') ||
      name.includes('celular') ||
      name.includes('bts ')
    ) {
      return TelecomCategory.MOBILE;
    }
    
    // 8. Si tiene telecom=* genérico, asumir móvil (más común)
    if (tags['telecom'] === 'antenna' || tags['telecom'] === 'mast') {
      return TelecomCategory.MOBILE;
    }
    
    return TelecomCategory.UNKNOWN;
  }

  /**
   * Busca instalaciones deportivas en un municipio
   */
  public async findSportsFacilities(
    municipality: string,
    _province?: string
  ): Promise<OSMElement[]> {
    try {
      const queries = PTEL_TO_OSM_QUERIES[InfrastructureType.SPORTS];

      const query = `
        [out:json][timeout:60];
        area[name="${municipality}"][admin_level=8]->.searchArea;
        (
          ${buildOverpassAreaQuery(queries)};
        );
        out center;
      `;

      return await this.executeQuery(query, 1, OVERPASS_TIMEOUT_MUNICIPAL_MS);

    } catch (error) {
      _overpassLog(`Error buscando instalaciones deportivas en ${municipality}:`, error);
      return [];
    }
  }

  /**
   * Busca instalaciones industriales en un municipio
   */
  public async findIndustrialFacilities(
    municipality: string,
    _province?: string
  ): Promise<OSMElement[]> {
    try {
      const query = `
        [out:json][timeout:60];
        area[name="${municipality}"][admin_level=8]->.searchArea;
        (
          ${buildOverpassAreaQuery(INDUSTRIAL_QUERIES)};
        );
        out center;
      `;

      return await this.executeQuery(query, 1, OVERPASS_TIMEOUT_MUNICIPAL_MS);

    } catch (error) {
      _overpassLog(`Error buscando instalaciones industriales en ${municipality}:`, error);
      return [];
    }
  }

  /**
   * Busca cualquier amenity en un municipio
   */
  public async findAllAmenities(
    municipality: string,
    _province?: string
  ): Promise<OSMElement[]> {
    try {
      const query = `
        [out:json][timeout:90];
        area[name="${municipality}"][admin_level=8]->.searchArea;
        (
          nwr["amenity"](area.searchArea);
          nwr["leisure"](area.searchArea);
          nwr["building"="public"](area.searchArea);
        );
        out center;
      `;

      return await this.executeQuery(query, 1, OVERPASS_TIMEOUT_MUNICIPAL_MS);

    } catch (error) {
      _overpassLog(`Error buscando amenities en ${municipality}:`, error);
      return [];
    }
  }

  /**
   * Geocodifica usando Nominatim (búsqueda por texto libre)
   * Más simple pero menos preciso que Overpass
   */
  public async geocodeWithNominatim(
    searchText: string,
    municipality: string,
    province?: string
  ): Promise<GeocodingResult | null> {
    // Caché compartida de Nominatim (NOMFIX-a2): antes esta ruta repetía la
    // misma consulta cada vez; prefijo propio porque el resultado tiene otra forma.
    const claveCache = `ovp|${searchText}|${municipality}|${province ?? ''}`.toLowerCase();
    const enCache = leerCacheNominatim<GeocodingResult>(claveCache);
    if (enCache.acierto) return enCache.valor;

    try {
      // Construir query
      const query = `${searchText}, ${municipality}${province ? ', ' + province : ''}, España`;
      
      // FASE-A: compuerta por host compartida (antes: delay(1000) local que
      // ignoraba a NominatimGeocoder → ~2 req/s combinadas contra política 1/s)
      await acquireHostSlot(OverpassGeocoder.NOMINATIM_URL);

      const response = await this.nominatimInstance.get(`${OverpassGeocoder.NOMINATIM_URL}/search`, {
        params: {
          q: query,
          format: 'json',
          limit: 5,
          addressdetails: 1
        }
      });

      if (!response.data || response.data.length === 0) {
        guardarSinResultadoNominatim(claveCache); // contestó: no hay nada (1 h)
        return null;
      }

      // Tomar el primer resultado
      const result = response.data[0];
      
      // Convertir a UTM30
      const [x, y] = proj4(OverpassGeocoder.WGS84, OverpassGeocoder.UTM30, [
        parseFloat(result.lon),
        parseFloat(result.lat)
      ]);

      // Calcular confianza basada en importancia y tipo
      let confidence = 50;
      if (result.importance) {
        confidence = Math.min(90, Math.round(result.importance * 100));
      }

      const resultadoFinal: GeocodingResult = {
        x,
        y,
        confidence,
        source: 'Nominatim',
        matchedName: result.display_name,
        matchType: result.type,
        municipality,
        province: province || '',
        metadata: {
          osmId: result.osm_id,
          osmType: result.osm_type,
          class: result.class,
          type: result.type,
          importance: result.importance
        }
      };
      guardarAciertoNominatim(claveCache, resultadoFinal);
      return resultadoFinal;

    } catch (error) {
      // NOMFIX-a1c: un 429 deja el host en enfriamiento para todos los llamantes
      // (antes se tragaba sin más y la ruta de telecom seguía pidiendo).
      if ((error as { response?: { status?: number } })?.response?.status === 429) {
        reportRateLimited(OverpassGeocoder.NOMINATIM_URL, retryAfterHeaderOf(error));
      }
      _overpassLog('Error geocodificando vía Nominatim:', error);
      return null;
    }
  }

  /**
   * Construye query Overpass basada en opciones
   * 
   * FIX #4: Sintaxis corregida - filtro (area.searchArea) debe estar
   * DENTRO de cada statement, no al final del bloque union.
   */
  private async buildQuery(options: OverpassSearchOptions): Promise<string> {
    const { municipality, infrastructureType } = options;

    // Obtener queries OSM según tipo
    let osmQueries: string[];
    
    if (infrastructureType && PTEL_TO_OSM_QUERIES[infrastructureType]) {
      osmQueries = PTEL_TO_OSM_QUERIES[infrastructureType];
    } else {
      // Query genérica amplia
      osmQueries = [
        'nwr["amenity"]',
        'nwr["leisure"]',
        'nwr["building"="public"]'
      ];
    }

    // FIX #4: Añadir filtro de área a cada query individual
    const queryString = osmQueries
      .map(q => `${q}(area.searchArea)`)
      .join(';\n    ');

    return `
[out:json][timeout:60];
area[name="${municipality}"][admin_level=8]->.searchArea;
(
  ${queryString};
);
out center;
    `.trim();
  }

  /**
   * Única salida HTTP hacia Overpass (CORTEOVP): consulta el corte antes de
   * pedir y le informa del resultado. Devuelve `null` sin pedir nada si el
   * corte está abierto.
   */
  private async postOverpass(query: string, timeoutMs: number): Promise<{ data?: { elements?: OverpassOSMElement[] } } | null> {
    const paso = corteOverpassDejaPasar();
    if (paso === null) return null;
    try {
      const response = await this.axiosInstance.post(
        OverpassGeocoder.OVERPASS_URL,
        `data=${encodeURIComponent(query)}`,
        { timeout: timeoutMs }
      );
      registrarExitoOverpass();
      return response;
    } catch (error) {
      registrarFalloOverpass(error, paso === 'sonda');
      throw error;
    }
  }

  /**
   * Ejecuta query Overpass y parsea resultados.
   * BUG-UI-05: 1 reintento con backoff de 5 s para el 429.
   * CORTEOVP: el 504 ya no se reintenta; abre el corte (igual que un timeout,
   * 502 o 521). El resto de errores fallan inmediatamente.
   */
  private async executeQuery(
    query: string,
    attempt = 1,
    timeoutMs: number = OVERPASS_TIMEOUT_FILA_MS
  ): Promise<OSMElement[]> {
    try {
      const response = await this.postOverpass(query, timeoutMs);

      if (!response || !response.data || !response.data.elements) {
        return [];
      }

      return response.data.elements
        .map((el: OverpassOSMElement) => this.parseElement(el))
        .filter((el: OSMElement | null): el is OSMElement => el !== null);

    } catch (error) {
      // BUG-UI-05: reintento gestionado para el 429 (CORTEOVP: el 504 abre el corte)
      const status: number | undefined =
        (error as { response?: { status?: number } })?.response?.status;

      if (status === 429 && attempt === 1) {
        const backoffMs = 5000;
        if (!_IS_TEST) {
          console.warn(
            `[OverpassGeocoder] HTTP ${status} — reintentando en ${backoffMs / 1000} s`
          );
        }
        await this.delay(backoffMs);
        return this.executeQuery(query, 2, timeoutMs);
      }

      _overpassLog('Error ejecutando query Overpass:', error);
      return [];
    }
  }

  /**
   * Parsea elemento OSM a formato interno
   */
  private parseElement(element: OverpassOSMElement): OSMElement | null {
    try {
      // Obtener coordenadas (directas para node, center para way/relation)
      let lat: number, lon: number;

      if (element.type === 'node' && element.lat !== undefined && element.lon !== undefined) {
        lat = element.lat;
        lon = element.lon;
      } else if (element.center) {
        lat = element.center.lat;
        lon = element.center.lon;
      } else {
        return null;
      }

      // Extraer nombre
      const tags = element.tags || {};
      const name = tags.name || tags['name:es'] || tags.alt_name || '';

      if (!name) {
        return null; // Ignorar elementos sin nombre
      }

      return {
        id: element.id,
        type: element.type,
        name,
        lat,
        lon,
        tags
      };

    } catch (_error) {
      return null;
    }
  }

  /**
   * Parsea elemento OSM incluyendo anónimos (sin nombre)
   * F3.T3: Para infraestructuras telecom que solo tienen coordenadas
   */
  private parseElementIncludingAnonymous(element: OverpassOSMElement): (OSMElement & { anonymous?: boolean }) | null {
    try {
      let lat: number, lon: number;

      if (element.type === 'node' && element.lat !== undefined && element.lon !== undefined) {
        lat = element.lat;
        lon = element.lon;
      } else if (element.center) {
        lat = element.center.lat;
        lon = element.center.lon;
      } else {
        return null;
      }

      const tags = element.tags || {};
      const name = tags.name || tags['name:es'] || tags.alt_name || '';
      
      // Generar nombre descriptivo para anónimos
      const generatedName = name || this.generateAntennaName(tags, element.id);

      return {
        id: element.id,
        type: element.type,
        name: generatedName,
        lat,
        lon,
        tags,
        anonymous: !name
      };

    } catch (_error) {
      return null;
    }
  }

  /**
   * Genera nombre descriptivo para antenas/torres sin nombre
   */
  private generateAntennaName(tags: Record<string, string>, id: number): string {
    // Intentar describir por tipo
    if (tags['tower:type'] === 'communication') {
      return `Torre Comunicaciones #${id}`;
    }
    if (tags['man_made'] === 'mast') {
      return `Mástil #${id}`;
    }
    if (tags['man_made'] === 'tower') {
      return `Torre #${id}`;
    }
    if (tags['telecom'] === 'antenna') {
      return `Antena #${id}`;
    }
    if (tags['telecom'] === 'mast') {
      return `Mástil Telecom #${id}`;
    }
    if (tags['telecom'] === 'base_station') {
      return `Estación Base #${id}`;
    }
    if (tags['communication:mobile_phone'] === 'yes') {
      return `Instalación Móvil #${id}`;
    }
    
    return `Infraestructura Telecom #${id}`;
  }

  /**
   * Encuentra el mejor match usando fuzzy matching
   */
  private findBestMatch(
    searchName: string,
    elements: OSMElement[]
  ): { element: OSMElement; score: number } | null {
    if (elements.length === 0) {
      return null;
    }

    const fuzzy = new FastFuzzy(elements as unknown as Record<string, unknown>[], {
      keys: ['name'],
      threshold: 0.6 // Más permisivo para OSM
    });

    const results = fuzzy.search(searchName);

    if (results.length === 0) {
      // Si no hay match fuzzy, retornar el primero si hay pocos resultados
      if (elements.length <= 3) {
        return { element: elements[0], score: 0.4 };
      }
      return null;
    }

    const best = results[0];
    return {
      element: best.item as unknown as OSMElement,
      score: 1 - (best.score || 1)
    };
  }

  /**
   * Convierte coordenadas WGS84 a UTM30
   */
  public toUTM30(lon: number, lat: number): { x: number; y: number } {
    const [x, y] = proj4(OverpassGeocoder.WGS84, OverpassGeocoder.UTM30, [lon, lat]);
    return { x, y };
  }

  /**
   * Convierte coordenadas UTM30 a WGS84
   */
  public toWGS84(x: number, y: number): { lon: number; lat: number } {
    const [lon, lat] = proj4(OverpassGeocoder.UTM30, OverpassGeocoder.WGS84, [x, y]);
    return { lon, lat };
  }

  /**
   * Delay para respetar límites de rate
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Limpia caché de áreas
   */
  public clearCache(): void {
    this.areaCache.clear();
  }

  /**
   * Estadísticas del geocodificador para testing/monitorización
   */
  public getStats(): {
    endpoint: string;
    nominatimEndpoint: string;
    rateLimitMs: number;
    cacheSize: number;
    requestCount: number;
  } {
    return {
      endpoint: OverpassGeocoder.OVERPASS_URL,
      nominatimEndpoint: OverpassGeocoder.NOMINATIM_URL,
      rateLimitMs: 1000, // 1 req/s para Nominatim
      cacheSize: this.areaCache.size,
      requestCount: 0
    };
  }

  /**
   * Verifica si se puede hacer una petición según rate limit
   */
  public checkRateLimit(): boolean {
    // Por ahora siempre true - rate limit se gestiona con delay()
    return true;
  }

  /**
   * Geocodifica una torre de comunicación específica
   * Wrapper para TorresComGeocoder
   * 
   * @param options - Opciones de búsqueda
   * @returns Resultado de geocodificación o null
   */
  public async geocodeTower(options: OverpassSearchOptions): Promise<GeocodingResult | null> {
    // Forzar tipo TELECOM para torres
    return this.geocode({
      ...options,
      infrastructureType: InfrastructureType.TELECOM
    });
  }

  /**
   * Obtiene todas las torres de comunicación en un área
   * 
   * @param municipality - Nombre del municipio
   * @param bbox - BBOX en EPSG:25830 [minX, minY, maxX, maxY]
   * @returns Array de features con coordenadas UTM30
   */
  public async getTowersInArea(
    municipality: string,
    bbox: [number, number, number, number]
  ): Promise<Array<{ name: string; x: number; y: number; id: number; properties: Record<string, unknown> }>> {
    try {
      // Convertir BBOX de UTM30 a WGS84
      const [minX, minY, maxX, maxY] = bbox;
      const sw = this.toWGS84(minX, minY);
      const ne = this.toWGS84(maxX, maxY);
      
      // Query por BBOX (más eficiente que por área para lotes grandes)
      const query = `
        [out:json][timeout:60];
        (
          ${TELECOM_QUERY}
        )(${sw.lat},${sw.lon},${ne.lat},${ne.lon});
        out center;
      `;

      const elements = await this.executeQuery(query, 1, OVERPASS_TIMEOUT_MUNICIPAL_MS);
      
      // Convertir a formato con coordenadas UTM30
      return elements.map(el => {
        const { x, y } = this.toUTM30(el.lon, el.lat);
        return {
          name: el.name,
          x,
          y,
          id: el.id,
          properties: {
            osmType: el.type,
            tags: el.tags,
            municipality
          }
        };
      });

    } catch (error) {
      _overpassLog(`Error obteniendo torres OSM en ${municipality}:`, error);
      return [];
    }
  }
}